import type { SelectItem } from "@fronterahealth/frontera-ui-components";

import type { AssessmentToolType } from "@api/graphql/types-and-hooks";

export interface AssessmentSelectItem extends SelectItem {
  supportedFileTypes: string[];
}

export const getFileExtension = (fileName: string): string | undefined =>
  /\.([a-zA-Z0-9]+)$/.exec(fileName)?.[1]?.toLowerCase();

export const nonNullStrings = (values: ReadonlyArray<string | null | undefined> | null | undefined): string[] =>
  (values ?? []).filter((value): value is string => !!value);

const supportedTypesOf = (assessment: Pick<AssessmentToolType, "supportedFileTypes">): string[] =>
  nonNullStrings(assessment.supportedFileTypes).map((type) => type.toLowerCase());

// No declared supportedFileTypes means the backend hasn't restricted the format, so we
// don't block on it.
export const isFileTypeSupportedForAssessment = (
  fileName: string,
  assessment: Pick<AssessmentToolType, "supportedFileTypes">,
): boolean => {
  const supported = supportedTypesOf(assessment);
  if (supported.length === 0) return true;
  const extension = getFileExtension(fileName);
  return !!extension && supported.includes(extension);
};

export interface FileTypeError {
  fileName: string;
  message: string;
}

// Computed purely from what's already on the client (the file's own name and the
// supportedFileTypes the assessmentTools query returned) — independent of whatever
// failureReason the backend may or may not have set on the LearnerFile itself.
export const computeFileTypeErrors = (
  file: { fileName: string; fileKind?: string },
  supportedAssessments: AssessmentToolType[],
): FileTypeError[] => {
  const assessment = supportedAssessments.find((candidate) => candidate.id === file.fileKind);
  if (!assessment || isFileTypeSupportedForAssessment(file.fileName, assessment)) return [];
  const types = supportedTypesOf(assessment);
  return [
    {
      fileName: file.fileName,
      message: `${assessment.primary} only supports ${types.map((type) => type.toUpperCase()).join(", ")} file type${types.length === 1 ? "" : "s"}.`,
    },
  ];
};
